import React, { useRef, useState, useEffect, useContext } from 'react';
import EmailEditor from 'react-email-editor';
import { Save, Download, Eye, FolderOpen, Trash2, X } from 'lucide-react';
import AuthContext from 'context/AuthContext';
import useCan from 'hooks/useCan';
import { getHostRecruitmentTemplateVariables } from 'Services/storeService';
import { CAPABILITIES } from 'utils/capabilities';

/**
 * Unlayer Email Editor Demo
 * Professional email builder with watermark in free tier
 */
const UnlayerDemo = () => {
  console.log('🚀 UnlayerDemo component loaded!');
  
  const emailEditorRef = useRef(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [variables, setVariables] = useState([]);
  const [editorReady, setEditorReady] = useState(false);
  
  const { userProfile } = useContext(AuthContext);
  const storeId = userProfile?.store?.id;
  const { allowed: canManageCommunications } = useCan(CAPABILITIES.COMMUNICATIONS_MANAGE);
  
  console.log('Store ID:', storeId);

  // Load saved templates from localStorage on mount
  useEffect(() => {
    console.log('📋 Loading saved templates...');
    loadSavedTemplates();
  }, []);

  // Fetch variables from API
  useEffect(() => {
    console.log('🔄 Variables fetch effect triggered. Store ID:', storeId);
    if (storeId) {
      console.log('✅ Store ID found, fetching variables...');
      fetchVariables();
    } else {
      console.log('⚠️ No store ID, skipping variables fetch');
    }
  }, [storeId]);

  // Update merge tags when both editor is ready AND variables are loaded
  useEffect(() => {
    if (editorReady && emailEditorRef.current && variables.length > 0) {
      console.log('Editor ready and variables loaded, setting merge tags:', variables);
      
      try {
        const editor = emailEditorRef.current.editor;
        
        if (editor && typeof editor.setMergeTags === 'function') {
          const mergeTags = {};
          
          variables.forEach(variable => {
            mergeTags[variable.value] = {
              name: variable.label,
              value: `{{${variable.value}}}`,
              sample: variable.example || 'Sample'
            };
          });
          
          console.log('Setting merge tags:', Object.keys(mergeTags).length, 'variables');
          editor.setMergeTags(mergeTags);
          console.log('✅ Merge tags set successfully!');
        } else {
          console.warn('Editor.setMergeTags not available');
        }
      } catch (error) {
        console.error('❌ Error setting merge tags:', error);
      }
    } else {
      console.log('Waiting for editor and variables...', {
        editorReady,
        hasEditor: !!emailEditorRef.current,
        variablesCount: variables.length
      });
    }
  }, [editorReady, variables]);

  const loadSavedTemplates = () => {
    try {
      const templates = JSON.parse(localStorage.getItem('unlayer_templates') || '[]');
      setSavedTemplates(templates);
    } catch (error) {
      console.error('Error loading templates:', error);
      setSavedTemplates([]);
    }
  };

  const fetchVariables = async () => {
    if (!storeId) {
      console.log('❌ Cannot fetch variables: no store ID');
      return;
    }
    
    console.log('📥 Fetching variables from API for store:', storeId);
    try {
      const response = await getHostRecruitmentTemplateVariables(storeId);
      console.log('📦 API response:', response);
      
      if (response?.data && Array.isArray(response.data)) {
        const vars = response.data.map(v => ({
          label: v.label || v.name,
          value: v.value || v.key,
          example: v.example || 'Sample'
        }));
        console.log('✅ Variables mapped:', vars.length, 'variables');
        console.log('Variables:', vars);
        setVariables(vars);
      } else {
        console.warn('⚠️ No valid data in response, using fallback');
        setVariables([
          { label: 'First Name', value: 'first_name', example: 'John' },
          { label: 'Last Name', value: 'last_name', example: 'Doe' },
          { label: 'Email', value: 'email', example: 'john@example.com' }
        ]);
      }
    } catch (error) {
      console.error('❌ Error fetching variables:', error);
      // Fallback to default variables
      console.log('Using fallback variables');
      setVariables([
        { label: 'First Name', value: 'first_name', example: 'John' },
        { label: 'Last Name', value: 'last_name', example: 'Doe' },
        { label: 'Email', value: 'email', example: 'john@example.com' }
      ]);
    }
  };

  const onReady = () => {
    console.log('🎉 Unlayer editor ready!');
    console.log('Variables available on ready:', variables.length);
    
    // Mark editor as ready
    setEditorReady(true);
    
    // Add custom merge tags (variables) from API
    if (emailEditorRef.current) {
      const editor = emailEditorRef.current.editor;
      
      // Build merge tags object from fetched variables
      const mergeTags = {};
      
      if (variables && variables.length > 0) {
        console.log('Using API variables for merge tags');
        variables.forEach(variable => {
          mergeTags[variable.value] = {
            name: variable.label,
            value: `{{${variable.value}}}`,
            sample: variable.example || 'Sample'
          };
        });
      } else {
        // Fallback merge tags (will be updated when variables load)
        console.log('Using fallback merge tags, will update when variables load');
        mergeTags.first_name = {
          name: 'First Name',
          value: '{{first_name}}',
          sample: 'John'
        };
        mergeTags.last_name = {
          name: 'Last Name',
          value: '{{last_name}}',
          sample: 'Doe'
        };
        mergeTags.email = {
          name: 'Email Address',
          value: '{{email}}',
          sample: 'john@example.com'
        };
        mergeTags.store_name = {
          name: 'Store Name',
          value: '{{store_name}}',
          sample: 'Your Store'
        };
      }
      
      console.log('Setting initial merge tags (fallback):', Object.keys(mergeTags).length, 'tags');
      try {
        editor.setMergeTags(mergeTags);
        console.log('✅ Initial merge tags set');
      } catch (error) {
        console.error('❌ Error setting initial merge tags:', error);
      }

      // Load Purebrand Host Recruitment template
      editor.loadDesign({
        body: {
          rows: [
            {
              cells: [1],
              columns: [
                {
                  contents: [
                    {
                      type: 'image',
                      values: {
                        containerPadding: '30px 40px 0px',
                        backgroundColor: '#3D9AD1',
                        src: {
                          url: 'https://dummyimage.com/180x50/ffffff/3d9ad1&text=PUREBRAND',
                          width: 180,
                          height: 50
                        },
                        textAlign: 'center',
                        altText: 'Purebrand Logo',
                        _meta: {
                          htmlID: 'u_logo',
                          htmlClassNames: 'u_logo'
                        }
                      }
                    }
                  ]
                }
              ]
            },
            {
              cells: [1],
              columns: [
                {
                  contents: [
                    {
                      type: 'text',
                      values: {
                        containerPadding: '30px 40px 50px',
                        backgroundColor: '#3D9AD1',
                        _meta: {
                          htmlID: 'u_header',
                          htmlClassNames: 'u_header'
                        },
                        text: '<h1 style="margin: 0px 0px 12px; line-height: 120%; text-align: center; word-wrap: break-word; font-size: 36px; font-weight: 700; color: #ffffff;">Join Purebrand as a Host! 🎉</h1><p style="font-size: 18px; line-height: 150%; text-align: center; word-wrap: break-word; margin: 0; color: rgba(255, 255, 255, 0.95);">Hi <span style="background-color: rgba(255, 255, 255, 0.2); padding: 4px 10px; border-radius: 6px; font-weight: 600;">{{first_name}}</span>, we\'d love to have you!</p>'
                      }
                    }
                  ]
                }
              ]
            },
            {
              cells: [1],
              columns: [
                {
                  contents: [
                    {
                      type: 'text',
                      values: {
                        containerPadding: '45px 40px 30px',
                        text: '<h2 style="color: #1e293b; font-size: 24px; margin: 0 0 20px 0; font-weight: 700;">Why Become a Purebrand Host?</h2>'
                      }
                    }
                  ]
                }
              ]
            },
            {
              cells: [1],
              columns: [
                {
                  contents: [
                    {
                      type: 'text',
                      values: {
                        containerPadding: '0px 40px 20px',
                        text: '<div style="display: flex; align-items: flex-start; margin-bottom: 20px;"><div style="flex-shrink: 0; width: 48px; height: 48px; background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%); border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-right: 16px;"><span style="font-size: 24px;">💰</span></div><div><h3 style="color: #334155; font-size: 18px; margin: 0 0 8px 0; font-weight: 600;">Earn Extra Income</h3><p style="color: #64748b; font-size: 15px; margin: 0; line-height: 1.6;">Make money by hosting product demos and sharing your experience with customers.</p></div></div><div style="display: flex; align-items: flex-start; margin-bottom: 20px;"><div style="flex-shrink: 0; width: 48px; height: 48px; background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%); border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-right: 16px;"><span style="font-size: 24px;">🏠</span></div><div><h3 style="color: #334155; font-size: 18px; margin: 0 0 8px 0; font-weight: 600;">Work From Anywhere</h3><p style="color: #64748b; font-size: 15px; margin: 0; line-height: 1.6;">Host virtual or in-person events on your own schedule. Complete flexibility!</p></div></div><div style="display: flex; align-items: flex-start;"><div style="flex-shrink: 0; width: 48px; height: 48px; background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%); border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-right: 16px;"><span style="font-size: 24px;">🎁</span></div><div><h3 style="color: #334155; font-size: 18px; margin: 0 0 8px 0; font-weight: 600;">Exclusive Perks</h3><p style="color: #64748b; font-size: 15px; margin: 0; line-height: 1.6;">Get early access to new products, special discounts, and host-only rewards.</p></div></div>'
                      }
                    }
                  ]
                }
              ]
            },
            {
              cells: [1],
              columns: [
                {
                  contents: [
                    {
                      type: 'text',
                      values: {
                        containerPadding: '30px 40px',
                        backgroundColor: '#f0f9ff',
                        text: '<h3 style="color: #0c4a6e; font-size: 16px; margin: 0 0 20px 0; font-weight: 600; text-align: center; text-transform: uppercase; letter-spacing: 0.5px;">Your Potential Earnings</h3><div style="display: flex; justify-content: space-around; text-align: center;"><div><div style="color: #3D9AD1; font-size: 32px; font-weight: 700; margin: 0 0 5px 0;">$500+</div><div style="color: #0c4a6e; font-size: 13px; font-weight: 500;">Per Month</div></div><div><div style="color: #3D9AD1; font-size: 32px; font-weight: 700; margin: 0 0 5px 0;">15%</div><div style="color: #0c4a6e; font-size: 13px; font-weight: 500;">Commission</div></div><div><div style="color: #3D9AD1; font-size: 32px; font-weight: 700; margin: 0 0 5px 0;">24/7</div><div style="color: #0c4a6e; font-size: 13px; font-weight: 500;">Support</div></div></div>'
                      }
                    }
                  ]
                }
              ]
            },
            {
              cells: [1],
              columns: [
                {
                  contents: [
                    {
                      type: 'button',
                      values: {
                        containerPadding: '35px 40px',
                        href: {
                          name: 'web',
                          values: {
                            href: 'https://purebrand.com/apply',
                            target: '_blank'
                          }
                        },
                        buttonColors: {
                          color: '#FFFFFF',
                          backgroundColor: '#3D9AD1',
                          hoverColor: '#FFFFFF',
                          hoverBackgroundColor: '#2563eb'
                        },
                        size: {
                          autoWidth: true,
                          width: '100%'
                        },
                        textAlign: 'center',
                        lineHeight: '120%',
                        padding: '18px 48px',
                        border: {},
                        borderRadius: '10px',
                        _meta: {
                          htmlID: 'u_cta_button',
                          htmlClassNames: 'u_cta_button'
                        },
                        text: '<span style="font-size: 18px; line-height: 21.6px;"><strong>Apply to Become a Host →</strong></span>'
                      }
                    }
                  ]
                }
              ]
            },
            {
              cells: [1],
              columns: [
                {
                  contents: [
                    {
                      type: 'text',
                      values: {
                        containerPadding: '0px 40px 40px',
                        text: '<p style="color: #94a3b8; font-size: 13px; margin: 0; text-align: center;">No experience needed • Free training provided • Join 1,000+ hosts</p>'
                      }
                    }
                  ]
                }
              ]
            },
            {
              cells: [1],
              columns: [
                {
                  contents: [
                    {
                      type: 'text',
                      values: {
                        containerPadding: '0px 40px 40px',
                        backgroundColor: '#f8fafc',
                        text: '<div style="border-left: 4px solid #3D9AD1; padding: 25px 30px;"><p style="color: #475569; font-size: 15px; line-height: 1.7; margin: 0 0 15px 0; font-style: italic;">"Becoming a Purebrand host was the best decision! I\'ve earned over $2,000 this month while working from home and meeting amazing people."</p><div style="display: flex; align-items: center;"><div style="width: 40px; height: 40px; background: linear-gradient(135deg, #3D9AD1 0%, #2563eb 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: 700; margin-right: 12px;">S</div><div><div style="color: #1e293b; font-size: 14px; font-weight: 600; margin: 0;">Sarah Mitchell</div><div style="color: #64748b; font-size: 13px; margin: 0;">Top Host • San Francisco</div></div></div></div>'
                      }
                    }
                  ]
                }
              ]
            },
            {
              cells: [1],
              columns: [
                {
                  contents: [
                    {
                      type: 'text',
                      values: {
                        containerPadding: '35px 40px',
                        backgroundColor: '#f8fafc',
                        text: '<p style="color: #334155; font-size: 14px; margin: 0 0 8px 0; font-weight: 500; text-align: center;">Questions? We\'re here to help!</p><p style="color: #64748b; font-size: 14px; margin: 0 0 20px 0; text-align: center;">Email us at <a href="mailto:hosts@purebrand.com" style="color: #3D9AD1; text-decoration: none; font-weight: 600;">hosts@purebrand.com</a></p><div style="margin-bottom: 15px; text-align: center;"><a href="#" style="color: #64748b; text-decoration: none; font-size: 13px; margin: 0 8px;">About</a><span style="color: #cbd5e1;">•</span><a href="#" style="color: #64748b; text-decoration: none; font-size: 13px; margin: 0 8px;">FAQ</a><span style="color: #cbd5e1;">•</span><a href="#" style="color: #64748b; text-decoration: none; font-size: 13px; margin: 0 8px;">Unsubscribe</a></div><p style="color: #94a3b8; font-size: 12px; margin: 0; text-align: center;">© 2026 Purebrand. All rights reserved.</p>'
                      }
                    }
                  ]
                }
              ]
            }
          ]
        }
      });
    }
  };

  const handleSave = () => {
    setShowSaveModal(true);
  };

  const saveTemplate = () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (emailEditorRef.current) {
      emailEditorRef.current.editor.exportHtml((data) => {
        const { design, html } = data;
        
        const template = {
          id: Date.now().toString(),
          name: templateName,
          html,
          design,
          createdAt: new Date().toISOString(),
          editor: 'unlayer'
        };

        const templates = JSON.parse(localStorage.getItem('unlayer_templates') || '[]');
        templates.push(template);
        localStorage.setItem('unlayer_templates', JSON.stringify(templates));
        
        setSavedTemplates(templates);
        setShowSaveModal(false);
        setTemplateName('');
        alert('✅ Template saved successfully!');
      });
    }
  };

  const loadTemplate = (template) => {
    if (emailEditorRef.current && template.design) {
      emailEditorRef.current.editor.loadDesign(template.design);
      setShowLoadModal(false);
      alert('✅ Template loaded successfully!');
    }
  };

  const deleteTemplate = (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      const templates = savedTemplates.filter(t => t.id !== templateId);
      localStorage.setItem('unlayer_templates', JSON.stringify(templates));
      setSavedTemplates(templates);
    }
  };

  const handleExportHtml = () => {
    if (emailEditorRef.current) {
      emailEditorRef.current.editor.exportHtml((data) => {
        const { design, html } = data;
        console.log('Unlayer Export:', { design, html });
        
        // Create download
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'unlayer-email.html';
        a.click();
        URL.revokeObjectURL(url);
      });
    }
  };

  const handlePreview = () => {
    if (emailEditorRef.current) {
      emailEditorRef.current.editor.exportHtml((data) => {
        const { html } = data;
        const previewWindow = window.open('', '_blank');
        previewWindow.document.write(html);
        previewWindow.document.close();
      });
    }
  };

  return (
    <div className="unlayer-demo-container">
      <div className="demo-toolbar">
        <div className="demo-info">
          <div className="info-badge watermark-badge">
            ⚠️ Free tier includes "Powered by Unlayer" watermark
          </div>
        </div>
        <div className="demo-actions">
          {canManageCommunications && (
          <button className="demo-button" onClick={() => setShowLoadModal(true)}>
            <FolderOpen size={16} />
            Load Template ({savedTemplates.length})
          </button>
          )}
          <button className="demo-button" onClick={handlePreview}>
            <Eye size={16} />
            Preview
          </button>
          <button className="demo-button" onClick={handleExportHtml}>
            <Download size={16} />
            Export HTML
          </button>
          {canManageCommunications && (
          <button className="demo-button-primary" onClick={handleSave}>
            <Save size={16} />
            Save Template
          </button>
          )}
        </div>
      </div>

      <div className="editor-wrapper">
        <EmailEditor
          ref={emailEditorRef}
          onReady={onReady}
          minHeight="calc(100vh - 280px)"
          options={{
            displayMode: 'email',
            projectId: 1234,
            mergeTags: {
              first_name: {
                name: 'First Name',
                value: '{{first_name}}',
                sample: 'John'
              },
              last_name: {
                name: 'Last Name', 
                value: '{{last_name}}',
                sample: 'Doe'
              },
              email: {
                name: 'Email',
                value: '{{email}}',
                sample: 'john@example.com'
              }
            },
            features: {
              textEditor: {
                spellChecker: true,
                tables: true
              },
              imageEditor: true,
              undoRedo: true,
              stockImages: {
                enabled: true
              }
            },
            tools: {
              form: {
                enabled: true
              }
            },
            specialLinks: {
              enabled: true
            },
            appearance: {
              theme: 'light',
              panels: {
                tools: {
                  dock: 'left'
                }
              }
            }
          }}
        />
      </div>

      <div className="demo-footer">
        <div className="footer-note">
          <strong>Note:</strong> Unlayer is very polished and professional, but the free version adds a watermark.
          To remove it, you need to upgrade to a paid plan ($49-$199/month).
        </div>
      </div>

      {/* Save Template Modal */}
      {showSaveModal && (
        <div className="template-modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="template-modal-header">
              <h3>Save Template</h3>
              <button className="modal-close-btn" onClick={() => setShowSaveModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="template-modal-body">
              <label className="template-input-label">Template Name</label>
              <input
                type="text"
                className="template-input"
                placeholder="e.g., Welcome Email, Order Confirmation"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && saveTemplate()}
                autoFocus
              />
            </div>
            <div className="template-modal-footer">
              <button className="template-btn-cancel" onClick={() => setShowSaveModal(false)}>
                Cancel
              </button>
              <button className="template-btn-primary" onClick={saveTemplate}>
                <Save size={16} />
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Template Modal */}
      {showLoadModal && (
        <div className="template-modal-overlay" onClick={() => setShowLoadModal(false)}>
          <div className="template-modal template-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="template-modal-header">
              <h3>Load Template</h3>
              <button className="modal-close-btn" onClick={() => setShowLoadModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="template-modal-body">
              {savedTemplates.length === 0 ? (
                <div className="no-templates">
                  <FolderOpen size={48} />
                  <p>No saved templates yet</p>
                  <span>Save your first template to see it here</span>
                </div>
              ) : (
                <div className="templates-grid">
                  {savedTemplates.map((template) => (
                    <div key={template.id} className="template-card">
                      <div className="template-card-header">
                        <h4>{template.name}</h4>
                        <button
                          className="template-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTemplate(template.id);
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="template-card-meta">
                        <span>Unlayer</span>
                        <span>•</span>
                        <span>{new Date(template.createdAt).toLocaleDateString()}</span>
                      </div>
                      <button
                        className="template-load-btn"
                        onClick={() => loadTemplate(template)}
                      >
                        Load Template
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnlayerDemo;
